/**
 * Runtime environment configuration.
 *
 * In Cloud Run: values come from window.__CONFIG__ which is generated by
 * start.sh at container startup from Cloud Run environment variables.
 *
 * In local dev: falls back to Vite build-time env vars from .env file.
 */

/**
 * Per-environment branding mask (SOPHIA-35093). When set, the portal shows the
 * override institution name and/or hides the partner logo everywhere the real
 * branding would render. Only staging sets it (partner demos to prospects);
 * dev/prd omit the key and behave exactly as before.
 */
export interface BrandingOverride {
  institutionName?: string;
  hideLogo?: boolean;
}

interface RuntimeConfig {
  apiUrl?: string;
  tokenApiUrl?: string;
  partnersApiUrl?: string;
  studentApiUrl?: string;
  transcriptApiUrl?: string;
  brandingOverride?: BrandingOverride | null;
  firebase?: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
  };
}

const runtime: RuntimeConfig = (window as Window & { __CONFIG__?: RuntimeConfig }).__CONFIG__ ?? {};

/** Accepts an object (runtime config.js) or a JSON string (local VITE_ env var). */
export function parseBrandingOverride(value: unknown): BrandingOverride | null {
  let raw = value;
  if (typeof raw === 'string') {
    if (!raw.trim()) return null;
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (raw == null || typeof raw !== 'object') return null;
  const candidate = raw as { institutionName?: unknown; hideLogo?: unknown };
  const override: BrandingOverride = {};
  if (typeof candidate.institutionName === 'string' && candidate.institutionName.trim()) {
    override.institutionName = candidate.institutionName;
  }
  if (candidate.hideLogo === true) {
    override.hideLogo = true;
  }
  return override.institutionName || override.hideLogo ? override : null;
}

export const env = {
  apiUrl: runtime.apiUrl || import.meta.env.VITE_API_URL || 'http://localhost:8080',
  tokenApiUrl: runtime.tokenApiUrl || import.meta.env.VITE_TOKEN_API_URL || 'http://localhost:8082',
  partnersApiUrl:
    runtime.partnersApiUrl || import.meta.env.VITE_PARTNERS_API_URL || 'http://localhost:8081',
  studentApiUrl:
    runtime.studentApiUrl || import.meta.env.VITE_STUDENT_API_URL || 'http://localhost:8083',
  transcriptApiUrl:
    runtime.transcriptApiUrl || import.meta.env.VITE_TRANSCRIPT_API_URL || 'http://localhost:8084',
  brandingOverride: parseBrandingOverride(
    runtime.brandingOverride ?? import.meta.env.VITE_BRANDING_OVERRIDE
  ),
  firebase: {
    apiKey:
      runtime.firebase?.apiKey ||
      import.meta.env.VITE_FIREBASE_API_KEY ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain:
      runtime.firebase?.authDomain ||
      import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId:
      runtime.firebase?.projectId ||
      import.meta.env.VITE_FIREBASE_PROJECT_ID ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket:
      runtime.firebase?.storageBucket ||
      import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId:
      runtime.firebase?.messagingSenderId ||
      import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId:
      runtime.firebase?.appId ||
      import.meta.env.VITE_FIREBASE_APP_ID ||
      import.meta.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  },
};
